import { scaleLinear, scaleTime } from 'd3-scale';

import type {
  ChartV2Point,
  ChartV2ResolvedSeries,
  ChartV2Series,
} from '../types/chart-v2.types';
import {
  indexAtTime,
  isOhlcPoint,
  pointValue,
  timeAtIndex,
} from '../utils/helpers';
import { Y_DATA_PADDING } from './nice-ticks';

export interface Extent {
  min: number;
  max: number;
}

export interface Viewport {
  width: number;
  height: number;
  padding: {
    top: number;
    right: number;
    bottom: number;
    left: number;
  };
}

/** Default canvas insets — keep InsightsOverlay in sync with ChartCanvas. */
export const DEFAULT_CANVAS_PADDING: Viewport['padding'] = {
  top: 40,
  right: 12,
  bottom: 32,
  left: 0,
};
const DAY_MS = 1_000 * 60 * 60 * 24;

export interface SeriesScale {
  seriesId: string;
  yScale: (value: number) => number;
  yDomain: [number, number];
  color: string;
}

export interface Scales {
  xScale: (value: Date | number) => number;
  yScale: (value: number) => number;
  xInvert: (pixel: number) => number;
  xDomain: [number, number];
  yDomain: [number, number];
  plot: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
  perSeries: SeriesScale[];
  barTimeline: number[];
}

const pointInDomain = (p: ChartV2Point, domain: [number, number]): boolean =>
  p.time >= domain[0] && p.time <= domain[1];

/**
 * True when point `i` should shape the y-domain for `domain`: points inside
 * the window, plus the last point before the left edge *while its segment
 * still enters the window*. That mirrors v1's `getYAxisDomain`, which
 * floored the domain start index — on a fractional pan offset the left
 * buffer bar's segment is still partially visible, and dropping it from
 * the extent would make the y-axis rescale mid-bar as the point slides
 * off the edge. The right edge is asymmetric on purpose: v1 floored the
 * end index too, so a partially-entering point past `domain[1]` never
 * shapes the axis.
 * Assumes `data` is sorted ascending by `time` (every chartv2 series is).
 */
const pointShapesExtent = (
  data: ChartV2Point[],
  i: number,
  domain: [number, number]
): boolean => {
  const p = data[i];

  if (p.time > domain[1]) {
    return false;
  }

  if (p.time >= domain[0]) {
    return true;
  }

  const next = data[i + 1];

  return next != null && next.time > domain[0];
};

/**
 * Ordered union of bar timestamps across series, but only when the data is
 * intraday (sub-daily step). Returns `null` for daily+ data, where a plain
 * time scale suffices. Drives the gap-collapsing index x-scale so trading
 * gaps (overnight, weekends) don't leave dead horizontal space.
 *
 * Call this once for the whole chart, not per pane: the returned array *is*
 * the index space the x-scale maps by, so two panes built from different
 * timelines put the same timestamp on different pixels and their gridlines
 * drift apart. `ChartV2` owns the single instance every pane draws with.
 */
export const buildBarTimeline = (
  series: ChartV2ResolvedSeries[],
  dayMs = DAY_MS
): number[] => {
  // Sample every series rather than the first one holding data. One timeline
  // now serves every pane, so a sparse series listed first would otherwise
  // read as daily and drop the *whole chart* back to a time scale, re-opening
  // the trading gaps for the intraday series sharing it. Densest step wins.
  let minStep = Number.POSITIVE_INFINITY;

  for (const s of series) {
    const d = s.data;
    const sampleCount = Math.min(20, d.length - 1);

    for (let i = d.length - 1; i > d.length - 1 - sampleCount; i--) {
      const step = d[i].time - d[i - 1].time;

      if (step > 0 && step < minStep) {
        minStep = step;
      }
    }
  }

  // Also covers "no series has two points" — `minStep` stays infinite.
  if (minStep >= dayMs) {
    return [];
  }

  const set = new Set<number>();

  for (const s of series) {
    for (const p of s.data) {
      set.add(p.time);
    }
  }

  return Array.from(set).sort((a, b) => a - b);
};

const extentForSeries = (
  s: ChartV2Series,
  xDomain: [number, number]
): Extent => {
  let min = Number.POSITIVE_INFINITY;
  let max = Number.NEGATIVE_INFINITY;

  for (let i = 0; i < s.data.length; i++) {
    const p = s.data[i];

    if (p.time > xDomain[1]) {
      break;
    }

    if (!pointShapesExtent(s.data, i, xDomain)) {
      continue;
    }

    const lo = isOhlcPoint(p) ? p.low : pointValue(p);
    const hi = isOhlcPoint(p) ? p.high : pointValue(p);

    min = Math.min(min, lo);
    max = Math.max(max, hi);
  }

  if (s.threshold) {
    for (const value of [
      s.threshold.good,
      s.threshold.bad,
      s.threshold.neutral,
    ]) {
      if (typeof value === 'number' && Number.isFinite(value)) {
        min = Math.min(min, value);
        max = Math.max(max, value);
      }
    }
  }

  if (!Number.isFinite(min) || !Number.isFinite(max)) {
    return { min: 0, max: 1 };
  }

  if (min === max) {
    return { min: min - 1, max: max + 1 };
  }

  const pad = (max - min) * Y_DATA_PADDING;

  return { min: min - pad, max: max + pad };
};

const hasPointInDomain = (
  series: ChartV2Series,
  xDomain: [number, number]
): boolean => series.data.some(p => pointInDomain(p, xDomain));

export const computeYExtent = (
  series: ChartV2Series[],
  xDomain: [number, number]
): Extent => {
  // Series with no points in the visible window would otherwise pull
  // `extentForSeries` into its `{ 0, 1 }` fallback and compress the
  // merged y-domain down to the floor — visible at runtime as
  // "indexed-to-100" panes that look squashed when one of the series
  // has no data in the current zoom range. Filter those out.
  const extents = series
    .filter(s => hasPointInDomain(s, xDomain))
    .map(s => extentForSeries(s, xDomain));

  if (extents.length === 0) {
    return { min: 0, max: 1 };
  }

  return {
    min: Math.min(...extents.map(e => e.min)),
    max: Math.max(...extents.map(e => e.max)),
  };
};

export interface CreateScalesInput {
  viewport: Viewport;
  xDomain: [number, number];
  seriesWithColors: Array<{ series: ChartV2Series; color: string }>;
  /**
   * When true, every series shares a single y-domain computed from the
   * union of all series extents. Used by the `indexed` price-display mode
   * where all series render on a common `%` axis.
   */
  mergeYDomain?: boolean;
  barTimeline: number[];
}

type XFns = {
  xScale: (value: Date | number) => number;
  xInvert: (pixel: number) => number;
};

const buildIndexXFns = ({
  tl,
  xDomain,
  plotX,
  plotWidth,
}: {
  tl: number[];
  xDomain: [number, number];
  plotX: number;
  plotWidth: number;
}): XFns => {
  // `span` is the fractional bar-index distance between the domain edges
  // (a continuous version of "number of visible bars minus one"). Deriving
  // it from `indexAtTime` — rather than snapping the domain to the nearest
  // bar — is what lets pan/zoom position bars at sub-pixel precision
  // instead of jumping in whole-bar steps.
  const domainStartIdx = indexAtTime(tl, xDomain[0]);
  const domainEndIdx = indexAtTime(tl, xDomain[1]);
  const span = Math.max(domainEndIdx - domainStartIdx, 1e-6);
  // +1 so each visible bar gets an equal slot with half a slot of inset
  // padding on both edges (matches the previous "n equal slots" layout).
  const slotWidth = plotWidth / (span + 1);

  return {
    xScale: (value: Date | number): number => {
      const ms = value instanceof Date ? value.getTime() : value;
      const idx = indexAtTime(tl, ms);

      return plotX + (idx - domainStartIdx + 0.5) * slotWidth;
    },
    xInvert: (pixel: number): number => {
      const idx = domainStartIdx + (pixel - plotX) / slotWidth - 0.5;

      return timeAtIndex(tl, idx);
    },
  };
};

const buildTimeXFns = (
  xDomain: [number, number],
  plotX: number,
  plotWidth: number
): XFns => {
  const rawX = scaleTime()
    .domain([new Date(xDomain[0]), new Date(xDomain[1])])
    .range([plotX, plotX + plotWidth]);

  return {
    xScale: (value: Date | number): number => {
      const out = rawX(value instanceof Date ? value : new Date(value));

      return typeof out === 'number' ? out : Number.NaN;
    },
    xInvert: (pixel: number): number => rawX.invert(pixel).getTime(),
  };
};

export interface XScaleInput {
  xDomain: [number, number];
  plotX: number;
  plotWidth: number;
  barTimeline?: number[];
}

const selectXFns = ({
  xDomain,
  plotX,
  plotWidth,
  barTimeline,
}: XScaleInput): XFns => {
  // Index-based scale: each bar gets an equal slot, eliminating trading-session
  // gaps (overnight, weekends). Mirrors V1's scaleLinear([0, lastIdx]) approach.
  // Pass the full timeline — `buildIndexXFns` already clips via `indexAtTime`.
  // Filtering to domain bars drops neighbors that define gap extrapolation, so
  // a domain edge in a trading gap would disagree with pan/zoom (full tl).
  return barTimeline && barTimeline.length >= 2
    ? buildIndexXFns({ tl: barTimeline, xDomain, plotX, plotWidth })
    : buildTimeXFns(xDomain, plotX, plotWidth);
};

/**
 * The canvas plot's time→pixel mapping. Exported so overlays (e.g.
 * `InsightsOverlay`) can position DOM elements on the exact same x-axis as
 * the drawn bars — including the gap-collapsing index scale.
 */
export const createXScale = (
  input: XScaleInput
): ((value: Date | number) => number) => selectXFns(input).xScale;

export const createScales = (input: CreateScalesInput): Scales => {
  const { viewport, xDomain, seriesWithColors, mergeYDomain, barTimeline } =
    input;
  const { padding } = viewport;
  const plot = {
    x: padding.left,
    y: padding.top,
    width: Math.max(0, viewport.width - padding.left - padding.right),
    height: Math.max(0, viewport.height - padding.top - padding.bottom),
  };

  const { xScale, xInvert } = selectXFns({
    xDomain,
    plotX: plot.x,
    plotWidth: plot.width,
    barTimeline,
  });

  const sharedExtent = mergeYDomain
    ? computeYExtent(
        seriesWithColors.map(s => s.series),
        xDomain
      )
    : null;

  const perSeries: SeriesScale[] = seriesWithColors.map(({ series, color }) => {
    const extent = sharedExtent ?? extentForSeries(series, xDomain);
    const raw = scaleLinear()
      .domain([extent.min, extent.max])
      .range([plot.y + plot.height, plot.y]);

    const yScale = (v: number): number => {
      const out = raw(v);

      return typeof out === 'number' ? out : Number.NaN;
    };

    return {
      seriesId: series.id,
      yScale,
      yDomain: [extent.min, extent.max],
      color,
    };
  });

  const primary = perSeries[0];
  const yScale = primary ? primary.yScale : (v: number) => v;
  const yDomain: [number, number] = primary ? primary.yDomain : [0, 1];

  return {
    xScale,
    yScale,
    xInvert,
    xDomain,
    yDomain,
    plot,
    perSeries,
    barTimeline,
  };
};
